import { createAction } from '@ngrx/store';
import {
  zefActionPayload,
  zefActionFailPayload,
  zefActionSuccessPayload
} from '@zerops/zef/core';
import { ZagentAgentType, ZagentAuthType } from '@zerops/zerops/core/zerops-services';

/**
 * Wire-format types — must match
 *   /Users/fxck/www/zerops-web-api-new/src/types/recipe-handoff.ts
 * and the marketing-site equivalent. Keep these three in sync.
 */

/**
 * Legacy single-agent shape. Retained for backward compatibility while
 * producers cut over to `agentConfigs` (see RecipeHandoffPayload).
 * Mutually exclusive with `RecipeHandoffPayload.agentConfigs`.
 * @deprecated use `RecipeHandoffPayload.agentConfigs` + root-level container
 * toggles instead.
 */
export interface RecipeHandoffZagentConfig {
  enableAgent?: boolean;
  enableVscode?: boolean;
  agentType: ZagentAgentType;
  authType: ZagentAuthType;
  provider: string;
  apiToken?: string;
  enablePublicAccess?: boolean;
  vscodePassword?: string;
  selectedServices?: string[];
}

/**
 * Per-agent auth config — the VALUE in `agentConfigs`. The agent type
 * itself is the map KEY, so there is no `agentType` field here.
 *
 * Container-level toggles (`enableVscode`, `enablePublicAccess`,
 * `vscodePassword`, `selectedServices`) describe the zcp container, not
 * any individual agent, and live on `RecipeHandoffPayload` directly.
 */
export interface ZagentAgentConfig {
  authType: ZagentAuthType;
  provider: string;
  apiToken?: string;
}

/**
 * Tag used on `projectEntity.addOne` meta for greenfield handoffs so
 * the recipe-handoff effect can distinguish its own success action
 * from form-driven adds and route to the new project page.
 */
export const RECIPE_HANDOFF_GREENFIELD_TAG = 'recipe-handoff-greenfield';

/**
 * Two mutually-exclusive variants — matches the web-api wire format:
 *   - Recipe import: `recipeSlug` + `environmentName` (+ optional `recipeSourceUrl`).
 *   - Greenfield: `projectName` only — the fan-out effect calls
 *     `projectEntity.addOne` directly with zagent in meta. The
 *     centralized `onProjectAddSuccessDeployZcp$` in project-base
 *     handles ZCP deployment.
 */
export interface RecipeHandoffPayload {
  recipeSlug?: string;
  environmentName?: string;
  recipeSourceUrl?: string;
  /** GitHub repo URL for unauthed control-plane github deploys; the fan-out
   *  effect rebuilds a github-shaped import payload when this is present. */
  githubUrl?: string;
  projectName?: string;

  /** @deprecated use `agentConfigs` + root-level container fields. */
  zagent?: RecipeHandoffZagentConfig;

  /**
   * Peer-agent map: any subset of `ZagentAgentType` keys, each with its
   * own auth config. Mutually exclusive with `zagent`.
   */
  agentConfigs?: Partial<Record<ZagentAgentType, ZagentAgentConfig>>;

  /*
   * Container-level toggles for the new shape — set on the payload root
   * alongside `agentConfigs` (multi-agent deploy) OR standalone (IDE-only
   * deploy with no agents at all). Mutually exclusive with `zagent`, which
   * carries its own copies of the same fields.
   */
  enableVscode?: boolean;
  enablePublicAccess?: boolean;
  vscodePassword?: string;
  selectedServices?: string[];
}

/**
 * Trigger consumption of a handoff token returned to the app via
 * `?handoff=<uuid>` after signup/login. Single-use; calling twice
 * with the same id will yield a 404 on the second call.
 */
export const consumeRecipeHandoff = createAction(
  '[@zerops/zerops/core/recipe-handoff-base] consume',
  zefActionPayload<{ id: string }>()
);

export const consumeRecipeHandoffSuccess = createAction(
  '[@zerops/zerops/core/recipe-handoff-base] consume/success',
  zefActionSuccessPayload<RecipeHandoffPayload>()
);

export const consumeRecipeHandoffFail = createAction(
  '[@zerops/zerops/core/recipe-handoff-base] consume/fail',
  zefActionFailPayload
);

/**
 * Pending-handoff stash: a single id captured the moment the user lands on
 * the login/registration route carrying a handoff, then drained exactly once
 * when the session is fully authenticated (see RecipeHandoffBaseEffect). This
 * replaces the per-auth-method eager consume that mis-fired on 2FA/passkey/
 * multi-org logins. `source` distinguishes the two wire transports:
 *   - 'handoff': `id` is a raw `?handoff=<uuid>` token (external producer).
 *   - 'recipe':  `id` is the raw `?recipe=` JSON string (in-app producer); it
 *     may or may not embed a handoff (plain recipes carry only recipeSlug).
 */
export interface RecipeHandoffPendingState {
  id: string | null;
  source: 'handoff' | 'recipe' | null;
}

export const setPendingHandoff = createAction(
  '[@zerops/zerops/core/recipe-handoff-base] set-pending-handoff',
  (id: string) => ({ id })
);

export const setPendingRecipe = createAction(
  '[@zerops/zerops/core/recipe-handoff-base] set-pending-recipe',
  (id: string) => ({ id })
);

export const clearPendingHandoff = createAction(
  '[@zerops/zerops/core/recipe-handoff-base] clear-pending-handoff'
);
